import React, { useEffect, useRef, useState } from 'react';
import { Play, Pause, SkipBack, SkipForward, Rewind } from 'lucide-react';

/**
 * PlaybackBar — bottom-of-chart replay controls that let the operator
 * scrub through the loaded candle history bar by bar. Every indicator
 * on the chart re-computes at each step (they read from the sliced
 * candle array) so the operator watches the exact sequence of events
 * unfold — a killer eyeball for backtesting setups and screen-record
 * walkthroughs.
 *
 * Props:
 *   total        — total number of candles available
 *   idx          — current playback index (0 = only 1st candle visible)
 *   onIdxChange  — (nextIdx) => void, called on scrub + tick
 *   onClose      — called when the operator closes playback mode
 *
 * Play tick: capped at 30 FPS so long candle arrays still recompute
 * cleanly on modest hardware. Speeds 1× / 2× / 5× control the number
 * of candles advanced per animation frame.
 */
const SPEEDS = [1, 2, 5];
const FRAME_MS = 33; // ~30 FPS

export default function PlaybackBar({ total = 0, idx = 0, onIdxChange, onClose }) {
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const rafRef = useRef(null);
  const lastFrameRef = useRef(0);

  useEffect(() => {
    if (!playing) return;
    const tick = (ts) => {
      if (!lastFrameRef.current) lastFrameRef.current = ts;
      const dt = ts - lastFrameRef.current;
      if (dt >= FRAME_MS) {
        lastFrameRef.current = ts;
        onIdxChange?.((prevIdx) => {
          // Support both value and functional callers.
          const cur = typeof prevIdx === 'number' ? prevIdx : idx;
          const next = Math.min(total, cur + speed);
          if (next >= total) {
            setPlaying(false);
            return total;
          }
          return next;
        });
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      lastFrameRef.current = 0;
    };
  }, [playing, speed, total, idx, onIdxChange]);

  const clamp = (v) => Math.max(1, Math.min(total, v));

  return (
    <div
      data-testid="crypto-playback-bar"
      className="flex items-center gap-3 border-t border-[var(--border-default)] bg-[var(--bg-panel)] px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest"
    >
      <span className="text-cyan-sys font-display">// PLAYBACK</span>
      <button
        data-testid="crypto-playback-rewind"
        title="Reset to first candle"
        onClick={() => onIdxChange?.(1)}
        className="text-[var(--txt-secondary)] hover:text-cyan-sys"
      >
        <Rewind size={13} />
      </button>
      <button
        data-testid="crypto-playback-back"
        title="Back 1 bar"
        onClick={() => onIdxChange?.(clamp(idx - 1))}
        className="text-[var(--txt-secondary)] hover:text-cyan-sys"
      >
        <SkipBack size={13} />
      </button>
      <button
        data-testid={playing ? 'crypto-playback-pause' : 'crypto-playback-play'}
        title={playing ? 'Pause' : 'Play'}
        onClick={() => setPlaying((p) => !p)}
        className="text-cyan-sys hover:text-buy px-1"
      >
        {playing ? <Pause size={15} /> : <Play size={15} />}
      </button>
      <button
        data-testid="crypto-playback-fwd"
        title="Forward 1 bar"
        onClick={() => onIdxChange?.(clamp(idx + 1))}
        className="text-[var(--txt-secondary)] hover:text-cyan-sys"
      >
        <SkipForward size={13} />
      </button>
      {/* Speed picker */}
      <div className="flex items-center gap-1">
        {SPEEDS.map((s) => (
          <button
            key={s}
            data-testid={`crypto-playback-speed-${s}x`}
            onClick={() => setSpeed(s)}
            className={`px-1.5 py-0.5 border ${speed === s
              ? 'border-cyan-sys text-cyan-sys bg-cyan-sys/10'
              : 'border-[var(--border-default)] text-[var(--txt-secondary)] hover:border-cyan-sys'}`}
          >
            {s}×
          </button>
        ))}
      </div>
      {/* Scrubber */}
      <input
        data-testid="crypto-playback-scrubber"
        type="range"
        min={1}
        max={Math.max(1, total)}
        value={idx}
        onChange={(e) => onIdxChange?.(clamp(parseInt(e.target.value, 10)))}
        className="flex-1 accent-cyan-sys"
      />
      <span data-testid="crypto-playback-progress" className="text-[var(--txt-primary)] tabular-nums">
        {idx} / {total}
      </span>
      <button
        data-testid="crypto-playback-close"
        title="Exit playback mode"
        onClick={onClose}
        className="text-sell hover:text-red-400 border border-sell/40 px-2 py-0.5"
      >
        ✕ exit
      </button>
    </div>
  );
}
